<template>
  <div class="war-room-comment-create-form-editor" @paste="handlePaste">
    <div class="war-room-comment-create-form-editor__control">
      <div class="war-room-comment-create-form-editor__comment">
        <ProfileSelectorAvatar
          v-if="profile"
          size="md"
          :name="profile.slug"
          :avatar-url="profile.avatarURL"
          class="war-room-comment-create-form-editor__avatar"
        />
        <div
          ref="textInput"
          anal-click-name="war-room__comment__input__focus"
          :data-boost-usernames="boostUsernames"
          selectable="true"
          contenteditable="true"
          class="war-room-comment-create-form-editor__input"
          data-test="comment-editor-block"
          @input="handleInput"
          @keyDown="handleKeyDown"
          @focus="handleFocus"
          @blur="handleBlur"
        />
      </div>
      <div class="war-room-comment-create-form-editor__actions">
        <ProfileSelector
          class="war-room-comment-create-form-editor__profile-selector"
          type="comment"
          :is-disabled="isEdit"
          :is-anonymous="isAnonymous"
          @select="handleProfileChange"
        />

        <div v-if="!imageUrl" class="war-room-comment-create-form-editor__add-image-wrapper" @click="addImageClick">
          <AddImageSvg />
          <input
            type="file"
            class="war-room-comment-create-form-editor__add-image-input"
            accept="image/*"
            data-test="image-input"
            @change="onImageChange"
          />
        </div>
        <div v-else-if="imageUrl" class="war-room-comment-create-form-editor__added-image-wrapper">
          <NuxtImg
            :key="imageUrl"
            :provider="uploadedImageUrl ? 'local' : null"
            :src="imageUrl"
            alt=""
            width="40"
            height="40"
            fit="fill"
            class="war-room-comment-create-form-editor__added-image"
            :loading="!!uploadedImageUrl ? 'eager' : 'lazy'"
          />
          <div class="war-room-comment-create-form-editor__added-image-border" />
          <BravadoProgressChartV2
            v-if="isImageUploading"
            :percent="uploadingProgress"
            :size="40"
            :stroke-width="4"
            foreground-color="var(--gray-50)"
            background-color="var(--gray-50)"
            class="war-room-comment-create-form-editor__image-loader"
          />
          <RemoveImageSvg class="war-room-comment-create-form-editor__added-image-remove" @click="onRemoveClick" />
        </div>

        <div v-if="$slots.action" class="war-room-comment-create-form-editor__cta">
          <slot name="action" />
        </div>
      </div>
      <div v-if="$slots.toolbar" class="war-room-comment-create-form-editor__toolbar">
        <slot name="toolbar" />
      </div>
    </div>
    <div v-if="isPlaceholderVisible" class="war-room-comment-create-form-editor__placeholder">Add a comment</div>
  </div>
</template>

<script lang="ts" setup>
import Vue from 'vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useContext, watch } from '@nuxtjs/composition-api';
import 'lazysizes';
import ProfileSelector from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelector.vue';
import { getActiveStorageUrl } from '@/components/partials/war-room/WarRoomTextEditor/utils';
import AddImageSvg from '@/components/partials/war-room/WarRoomCommentCreateForm/images/add-image.vue.svg';
import RemoveImageSvg from '@/components/partials/war-room/WarRoomCommentCreateForm/images/remove-image.vue.svg';
import uploader from '@/components/utils/uploader';
import ProfileSelectorAvatar from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorAvatar.vue';
import type { Profile } from '@/components/partials/optional-anonymity/ProfileSelector/types';
import { MENTIONS_CONTAINER_CLASS } from '@/components/common/BravadoEditor/constants';
import BravadoProgressChartV2 from '~/components/common/BravadoProgressChartV2/BravadoProgressChartV2.vue';

type WarRoomCommentCreateFormEditorMode = 'create' | 'edit';
type WarRoomCommentCreateFormEditorProps = {
  imageKey?: string | null;
  isAnonymous?: boolean | null;
  mode?: WarRoomCommentCreateFormEditorMode;
  initialText?: string;
  boostUsernames?: Array<string>;
  autofocus?: boolean;
};
type WarRoomCommentCreateFormEditorEmits = {
  (event: 'image-uploading', payload: boolean): void;
  (event: 'text-change', payload: string): void;
  (event: 'image-change', payload: { media: string; key: string }): void;
  (event: 'is-anonymous-change', payload: { isAnonymous: boolean; loaded: boolean }): void;
  (event: 'image-remove' | 'post-shortcut-entered'): void;
};

const props = withDefaults(defineProps<WarRoomCommentCreateFormEditorProps>(), {
  imageKey: null,
  isAnonymous: null,
  mode: 'create',
  initialText: '',
  boostUsernames: () => [],
  autofocus: false,
});
const emit = defineEmits<WarRoomCommentCreateFormEditorEmits>();

const { $analytics, $sentryUtils, $auth } = useContext();

// Set contenteditable false for all mentions to keep delete full mention functionality by one key from tributejs
const interactMentiones = (string: string) => {
  return string.replace(/<a data-mention-user-username/gim, '<a contenteditable="false" data-mention-user-username');
};

const FILE_SIZE_LIMIT = 5;

const text = ref('');
const focusIn = ref(false);
const uploadedImageUrl = ref<string | null>(null);
const isImageUploading = ref(false);
const imageUploadingId = ref<number | null>(null);
const uploadingProgress = ref(0);
const profile = ref<Profile | null>(null);
const textInput = ref<HTMLElement | null>(null);

const isPlaceholderVisible = computed(() => !(text.value.replace(/^<br>/g, '').trim().length || focusIn.value));
const imageUrl = computed(() => uploadedImageUrl.value || (props.imageKey && getActiveStorageUrl(props.imageKey)));
const isEdit = computed(() => props.mode === 'edit');

function focusInput() {
  nextTick(() => {
    textInput.value?.focus();
  });
}

watch(
  () => props.autofocus,
  newVal => {
    if (newVal) focusInput();
  },
  { immediate: true },
);
watch(isImageUploading, newVal => {
  emit('image-uploading', newVal);
});

function onRemoveClick() {
  isImageUploading.value = false;

  imageUploadingId.value = null;
  uploadedImageUrl.value = null;

  emit('image-remove');
}

function clearEditor() {
  text.value = '';
  if (textInput.value) textInput.value.innerHTML = '';
  emit('text-change', '');

  onRemoveClick();
}

function setHTML(html: string) {
  if (textInput.value) {
    text.value = textInput.value.innerHTML = interactMentiones(html);
    emit('text-change', text.value);
  }
}

function trubuteReplacedhandler() {
  text.value = textInput.value?.innerHTML || '';
}

onMounted(() => {
  if (props.initialText) {
    setHTML(props.initialText);
    focusInput();
  }

  textInput.value?.addEventListener('tribute-replaced', trubuteReplacedhandler);
});

onBeforeUnmount(() => {
  textInput.value?.removeEventListener('tribute-replaced', trubuteReplacedhandler);
});

function handlePaste(e: ClipboardEvent) {
  // trying to paste image
  if (e.clipboardData?.items) {
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      if (!items[i].type.includes('image')) continue;

      onImageChange({ target: { files: [items[i].getAsFile()] } });

      e.preventDefault();
      e.stopPropagation();

      return;
    }
  }

  // if not image then trying to paste html/text
  const html = e.clipboardData?.getData('text/html');

  if (!html) return;

  e.preventDefault();
  e.stopPropagation();

  const text = html.replace(/(style="([^"]*)")/gi, '');
  document.execCommand('insertHTML', false, text);
}

function handleInput(e: { target: HTMLInputElement }) {
  text.value = e.target.innerHTML;
  emit('text-change', text.value);
}

function handleFocus() {
  focusIn.value = true;
}

function handleBlur() {
  focusIn.value = false;
}

function handleKeyDown(e: KeyboardEvent) {
  const isEnter = e.key === 'Enter';
  const isFnKey = e.keyCode === 10;
  const mentionsDropdown = document.querySelector(`.${MENTIONS_CONTAINER_CLASS}`);
  const isMentionsVisible = !mentionsDropdown?.getAttribute('style')?.match(/none/);

  // War Room user mentions opened - skip enter to select user
  if (isMentionsVisible && isEnter) {
    return;
  }
  // Enter - insert new line
  if (isEnter && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    document.execCommand('insertLineBreak');
    return;
  }
  // Ctrl + Enter/Fn - submit
  if ((e.ctrlKey || e.metaKey) && (isEnter || isFnKey)) {
    emit('post-shortcut-entered');
  }
}

function addImageClick() {
  $analytics?.logEvent('war-room__post__comment-add-image__click');
}

function onImageChange(event: { target: { value?: string; files: Array<File | null> } }) {
  const file = event.target.files[0];

  if (!file || !file.type.includes('image')) {
    return;
  }

  if (getSizeInMb(file.size) > FILE_SIZE_LIMIT) {
    try {
      Vue.prototype.$notice.error(`File should be less than ${FILE_SIZE_LIMIT}mb!`);
    } catch (e) {
      // Supress error
    }
    event.target.value = '';
    return;
  }

  uploadFile(file);
}

async function uploadFile(file: File) {
  try {
    const now = Date.now();

    isImageUploading.value = true;
    imageUploadingId.value = now;

    uploadingProgress.value = 0;

    uploadedImageUrl.value = URL.createObjectURL(file);
    const metadata = await uploader.upload({
      file,
      token: $auth.user?.token ?? '',
      onProgress: progress => {
        if (imageUploadingId.value === now) {
          uploadingProgress.value = progress * 100;
        }
      },
    });

    if (imageUploadingId.value !== now) {
      return;
    }

    emit('image-change', { media: metadata.signed_id, key: metadata.key });

    isImageUploading.value = false;
  } catch (e) {
    uploadedImageUrl.value = null;
    imageUploadingId.value = null;

    isImageUploading.value = false;

    try {
      Vue.prototype.$notice.error('Failed to upload image.');
    } catch (e) {
      // Supress error
    }

    $sentryUtils.error(e);
  }
}

function getSizeInMb(bytes: number) {
  return parseFloat((bytes / 1024 ** 2).toFixed(2));
}

function handleProfileChange({ isAnonymous, profile: userProfile }: { isAnonymous: boolean; profile: Profile }) {
  profile.value = userProfile;

  emit('is-anonymous-change', { isAnonymous, loaded: !!userProfile.slug });
}

defineExpose({
  clearEditor,
  setHTML,
});
</script>

<style scoped lang="scss">
.war-room-comment-create-form-editor {
  $this: &;

  position: relative;

  &__input {
    @include break-word;
    @include input-focus-transition;

    width: 100%;
    min-height: inherit;
    background: var(--theme-color-card);
    color: var(--theme-color-text);
    appearance: none;
    outline: none;
    font-size: 16px;
    line-height: 1.31;
    padding: 5px 0 20px;

    &::before {
      content: '\feff';
    }

    ::v-deep * {
      @include inherit-font;
    }
  }

  &__placeholder {
    position: absolute;
    top: 12px;
    left: 50px;
    height: 36px;
    padding: 10px 12px;
    color: var(--gray-50);
    z-index: 1;
    pointer-events: none;
    font-size: 16px;
    line-height: 1.31;

    @include mq('sm') {
      height: 40px;
    }
  }

  &__add-image-wrapper {
    color: var(--theme-color-sub-text);
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
    margin-left: auto;

    &:hover {
      color: var(--theme-sub_text-hover);
    }

    @include mq('sm') {
      height: 38px;
    }
  }

  &__add-image-input {
    width: 100%;
    height: 100%;
    position: absolute;
    opacity: 0;
    cursor: pointer;

    // stylelint-disable-next-line
    &::file-selector-button {
      cursor: pointer;
    }
  }

  &__added-image-wrapper {
    position: relative;
    margin-left: auto;
  }

  &__added-image {
    border-radius: 4px;
    object-fit: cover;
  }

  &__added-image-border {
    opacity: 0.2;
    position: absolute;
    left: 0;
    top: 0;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    border: 1px solid var(--gray-50);
  }

  &__image-loader {
    position: absolute;
    left: 3px;
    top: 3px;
    width: 32px;
    height: 32px;
  }

  &__added-image-remove {
    width: 18px;
    height: 18px;
    background: var(--theme-color-over-card);
    border: 2px solid var(--theme-color-card);
    border-radius: 50%;
    position: absolute;
    right: -6px;
    top: -6px;
    cursor: pointer;

    &:hover {
      background: var(--theme-color-over-card-hover);
    }
  }

  &__control {
    border: 1px solid var(--theme-color-border);
    background: var(--theme-color-card);
    padding: 16px 20px;
    border-radius: 12px;
  }

  &__actions {
    display: flex;
    align-items: center;
  }

  &__profile-selector {
    margin-right: 12px;
  }

  &__cta {
    margin-left: 24px;
  }

  &__toolbar {
    margin-top: 12px;
  }

  &__comment {
    display: flex;
  }

  &__avatar {
    margin-right: 8px;
  }
}
</style>
